import React from 'react';
import { motion } from "framer-motion";
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Link } from "wouter";
import { ChevronUp } from "lucide-react";

const TermsAndConditions = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
        {/* Animated Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <motion.div
            animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-[10%] -left-[5%] w-[35%] h-[35%] bg-primary/5 rounded-full blur-[100px]"
          />
          <motion.div
            animate={{ x: [0, -40, 0], y: [0, 80, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[10%] -right-[5%] w-[30%] h-[30%] bg-accent/5 rounded-full blur-[100px]"
          />
        </div>

        <section id="terms" className="relative z-10 pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              {/* Header */}
              <div className="text-center mb-16 space-y-4">
                <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
                  Terms and <span className="text-primary">Conditions</span>
                </h1>
                <p className="text-muted-foreground font-medium">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-muted/50 rounded-2xl p-8 md:p-12 space-y-8 text-foreground border border-border">
                
                {/* Platform Info */}
                <div className="p-4 bg-background border border-border rounded-lg mb-8">
                  <p className="text-muted-foreground"><strong>Platform:</strong> Quro (Website & Mobile Application)</p>
                  <p className="text-muted-foreground"><strong>Operated By:</strong> Quick Retail Online Private Limited ("Quro", "We", "Us", "Our")</p>
                  <p className="text-muted-foreground text-sm mt-1"><strong>FSSAI Reg. No.:</strong> 30260424124243466</p>
                </div>

                {/* Introduction */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">1. Introduction</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    Welcome to Quro. These Terms and Conditions ("Terms") govern your access to and use of the Quro platform, 
                    including our website, mobile application, and services (collectively, the "Service"). By accessing or using 
                    our Service, you agree to be bound by these Terms.
                  </p>
                  <p className="leading-relaxed mt-4 text-muted-foreground">
                    If you do not agree to these Terms, please do not use our Service. We reserve the right to modify these Terms 
                    at any time, and such modifications will be effective immediately upon posting.
                  </p>
                </div>

                {/* Acceptance of Terms */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">2. Acceptance of Terms</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    By creating an account, accessing, or using the Quro Service, you acknowledge that you have read, understood, 
                    and agree to be bound by these Terms and our Privacy Policy. If you are using the Service on behalf of an 
                    organization, you represent that you have the authority to bind that organization to these Terms.
                  </p>
                </div>

                {/* User Accounts */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">3. User Accounts</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    To use certain features of our Service, you must register for an account. You agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Maintain the security of your password and identification</li>
                    <li>Accept all responsibility for activities that occur under your account</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                  </ul>
                </div>

                {/* Use of Service */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">4. Use of Service</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Infringe upon the rights of others</li>
                    <li>Transmit any harmful, offensive, or inappropriate content</li>
                    <li>Interfere with or disrupt the Service or servers</li>
                    <li>Attempt to gain unauthorized access to any part of the Service</li>
                    <li>Use automated systems to access the Service without permission</li>
                  </ul>
                </div>

                {/* Products and Services */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">5. Products and Services</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Quro connects customers with local vendors, farmers, and sellers. We facilitate transactions but are not a party 
                    to the actual sale of products. You acknowledge that:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Product descriptions, prices, and availability are provided by sellers</li>
                    <li>We do not guarantee the quality, safety, or legality of products sold by third-party sellers</li>
                    <li>All transactions are between you and the seller</li>
                    <li>We may facilitate payment processing but are not responsible for product delivery or quality</li>
                  </ul>
                </div>

                {/* Payment Terms */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">6. Payment Terms</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    When you make a purchase through Quro, you agree to pay all charges associated with your order, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Product prices as listed by sellers</li>
                    <li>Delivery fees (if applicable)</li>
                    <li>Service fees (if applicable)</li>
                    <li>Any applicable taxes</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground">
                    All payments are processed securely through our payment partners. By providing payment information, you represent 
                    that you are authorized to use the payment method.
                  </p>
                </div>

                {/* Returns and Refunds */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">7. Returns and Refunds</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    Quro offers a quality promise with free returns. If you are not satisfied with a product, you may return it 
                    according to our <Link href="/cancellation-policy" className="text-primary hover:underline">return policy</Link>. Refund eligibility and processing times are subject to our return policy, 
                    which may be updated from time to time.
                  </p>
                </div>

                {/* Intellectual Property */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">8. Intellectual Property</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    The Service, including its original content, features, and functionality, is owned by Quro and is protected by 
                    international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not 
                    reproduce, distribute, modify, or create derivative works of any content from the Service without our express 
                    written permission.
                  </p>
                </div>

                {/* Limitation of Liability */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">9. Limitation of Liability</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    To the maximum extent permitted by law, Quro shall not be liable for any indirect, incidental, special, 
                    consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, 
                    or any loss of data, use, goodwill, or other intangible losses resulting from your use of the Service.
                  </p>
                </div>

                {/* Indemnification */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">10. Indemnification</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    You agree to indemnify, defend, and hold harmless Quro, its officers, directors, employees, and agents from any 
                    claims, damages, losses, liabilities, and expenses (including legal fees) arising out of or relating to your use 
                    of the Service, violation of these Terms, or infringement of any rights of another.
                  </p>
                </div>

                {/* Termination */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">11. Termination</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    We may terminate or suspend your account and access to the Service immediately, without prior notice, for any 
                    reason, including breach of these Terms. Upon termination, your right to use the Service will cease immediately.
                  </p>
                </div>

                {/* Governing Law */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">12. Governing Law</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    These Terms shall be governed by and construed in accordance with the laws of India, without regard to its 
                    conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction 
                    of the courts in Hyderabad, India.
                  </p>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">13. Contact Us</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    If you have any questions about these Terms and Conditions, please contact us at:
                  </p>
                  <div className="mt-4 p-4 bg-background border border-border rounded-lg">
                    <p className="font-semibold text-foreground">Quro</p>
                    <p className="text-muted-foreground">Email: <a href="mailto:q@quro.today" className="text-primary hover:text-primary/80 transition-colors">q@quro.today</a></p>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">Registered Office:</p>
                      <p>Quick Retail Online Private Limited</p>
                      <p>24-21, Ashoknagar</p>
                      <p>Ramachandra Puram, Hyderabad-502032</p>
                    </div>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">HQ Office:</p>
                      <p>Suite 406, 4th floor, MyScape Weave</p>
                      <p>Financial District, Nankramaguda</p>
                      <p>Hyderabad-500032</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Back to Top Button */}
            <div className="mt-12 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <span>Back to Top</span>
              </button>
            </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default TermsAndConditions;
